import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../Api";
import Popup from "../components/PopUP";

export default function AirportDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [airport, setAirport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [popupMessage, setPopupMessage] = useState("");

  useEffect(() => {
    const fetchAirportDetails = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/airplane-service/airport/${id}`);
        const data = response.data.data;
        
        // Safely mapping data whether it's flat (like list API) or nested (like PATCH API)
        setAirport({
          id: data.id || id,
          general: data.general_information || {
            name: data.name,
            type: data.type,
            url: data.website_url,
          },
          location: data.location || {
            lat: data.latitude_deg,
            lon: data.longitude_deg,
            city: data.city,
            state: data.state,
            country: data.country,
          },
          time: data.time_information || {
            tz: data.timezone,
            utc: data.utc_offset,
          },
          codes: data.codes || {
            iata: data.iata_code,
            icao: data.icao,
          },
          runway: data.runway_information || {
            count: data.number_of_runways,
            direction: data.runway_direction,
            length: data.runway_length,
            elev: data.elev,
            surface: data.runway_surface_type,
            terminals: data.number_of_terminals,
          },
          stats: data.operational_statistics || {
            movements: data.annual_movements,
            passengers: data.annual_passenger_traffic,
            weather: data.airport_weather,
          }
        });
      } catch (error) {
        console.error("Fetch Error:", error);
        setPopupMessage("Failed to load airport details.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAirportDetails();
    }
  }, [id]);

  // Helper component for displaying fields
  const InfoField = ({ label, value }) => (
    <div className="mb-3">
      <label className="text-muted fw-bold mb-1" style={{ fontSize: "13px" }}>{label}</label>
      <div className="fw-medium text-dark">
        {value !== null && value !== undefined && value !== "" ? value : "-"}
      </div>
    </div>
  );

  return (
    <div className="user-management-page">
      <Popup message={popupMessage} onClose={() => setPopupMessage("")} />
      <Sidebar />

      <div className="main-wrapper">
        <Header />

        <div className="content-wrapper">
          <section className="main-section spacer-y">
            <div className="container">
              <div className="row gy-4">
                <div className="col-xl-12">

                  {/* Header Banner */}
                  <div className="common_main_bg_wrapper">
                    <div className="common_main_heading_wrapper">
                      <div className="vstack">
                        <nav className="breadcrumb_nav">
                          <ul className="breadcrumb mb-0">
                            <li className="breadcrumb-item">
                              <Link to="/dashboard">Dashboard</Link>
                            </li>
                            <li className="breadcrumb-item">
                              <Link to="/airport">Airport Management</Link>
                            </li>
                            <li className="breadcrumb-item text-capitalize">
                              Airport Details
                            </li>
                          </ul>
                        </nav>
                        <h1>{airport?.general?.name || "Airport Details"}</h1>
                      </div>
                      
                      <div className="d-flex flex-end gap-2">
                        <button 
                          onClick={() => navigate("/airport")} 
                          className="secondary_btn"
                        >
                          Back to List
                        </button>
                        <button 
                          onClick={() => navigate(`/airport/update/${id}`)} 
                          className="secondary_btn"
                        >
                          Edit Details
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Details Content */}
                  {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : airport ? (
                    <div className="common-outer-card mt-4">
                      <div className="common_content_blk">
                        
                        {/* GENERAL INFORMATION */}
                        <h5 className="mb-4 border-bottom pb-2">General Information</h5>
                        <div className="row">
                          <div className="col-md-4">
                            <InfoField label="Airport Name" value={airport.general.name} />
                          </div>
                          <div className="col-md-4">
                            <InfoField label="Airport Type" value={airport.general.type} />
                          </div>
                          <div className="col-md-4">
                            <InfoField 
                              label="Website URL" 
                              value={airport.general.url ? (
                                <a href={airport.general.url} target="_blank" rel="noreferrer">
                                  {airport.general.url}
                                </a>
                              ) : "-"} 
                            />
                          </div>
                        </div>

                        {/* LOCATION DETAILS */}
                        <h5 className="mb-4 mt-4  border-bottom pb-2">Location Details</h5>
                        <div className="row">
                          <div className="col-md-3">
                            <InfoField label="City" value={airport.location.city} />
                          </div>
                          <div className="col-md-3">
                            <InfoField label="State / Province" value={airport.location.state} />
                          </div>
                          <div className="col-md-3">
                            <InfoField label="Country" value={airport.location.country} />
                          </div>
                          <div className="col-md-3">
                            <InfoField 
                              label="Coordinates" 
                              value={`${airport.location.lat || "-"}, ${airport.location.lon || "-"}`} 
                            />
                          </div>
                        </div>

                        {/* CODES & TIME */}
                        <h5 className="mb-4 mt-4  border-bottom pb-2">Codes & Time Information</h5>
                        <div className="row">
                          <div className="col-md-3">
                            <InfoField label="IATA Code" value={airport.codes.iata} />
                          </div>
                          <div className="col-md-3">
                            <InfoField label="ICAO Code" value={airport.codes.icao} />
                          </div>
                          <div className="col-md-3">
                            <InfoField label="Timezone" value={airport.time.tz} />
                          </div>
                          <div className="col-md-3">
                            <InfoField label="UTC Offset" value={airport.time.utc} />
                          </div>
                        </div>

                        {/* RUNWAY INFORMATION */}
                        <h5 className="mb-4 mt-4  border-bottom pb-2">Runway Information</h5>
                        <div className="row">
                          <div className="col-md-4">
                            <InfoField label="Number of Runways" value={airport.runway.count} />
                          </div>
                          <div className="col-md-4">
                            <InfoField label="Runway Direction" value={airport.runway.direction} />
                          </div>
                          <div className="col-md-4">
                            <InfoField label="Runway Length (m)" value={airport.runway.length} />
                          </div>
                          <div className="col-md-4">
                            <InfoField label="Surface Type" value={airport.runway.surface} />
                          </div>
                          <div className="col-md-4">
                            <InfoField label="Elevation (ft)" value={airport.runway.elev} />
                          </div>
                          <div className="col-md-4">
                            <InfoField label="Number of Terminals" value={airport.runway.terminals} />
                          </div>
                        </div>

                        {/* OPERATIONAL STATISTICS */}
                        <h5 className="mb-4 mt-4  border-bottom pb-2">Operational Statistics</h5>
                        <div className="row">
                          <div className="col-md-4">
                            <InfoField label="Annual Movements" value={airport.stats.movements} />
                          </div>
                          <div className="col-md-4">
                            <InfoField label="Annual Passenger Traffic" value={airport.stats.passengers} />
                          </div>
                          <div className="col-md-4">
                            <InfoField 
                              label="Weather Link" 
                              value={airport.stats.weather ? (
                                <a href={airport.stats.weather} target="_blank" rel="noreferrer">
                                  View Weather
                                </a>
                              ) : "-"} 
                            />
                          </div>
                        </div>

                      </div>
                    </div>
                  ) : (
                    <div className="text-center mt-5">
                      <h4 className="text-muted">Airport not found</h4>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}